import {
	Cartesian3,
	Cartographic,
	ImageryLayerFeatureInfo,
	WebMercatorProjection,
	type WebMapServiceImageryProvider
} from 'cesium';

/**
 * Riksantikvaren's WMS services run MapServer, whose GetFeatureInfo tolerance is a fixed number
 * of pixels *of the requested image* — 3 by default, which we confirmed empirically (a point
 * 3.3 m away is found at 1.17 m/px but missed at 1.0 m/px). Cesium's own picking asks for the
 * 256 px tile under the cursor, so the tolerance is whatever that tile's resolution happens to
 * be: ~15 cm at zoom 18. Nothing short of a pixel-perfect click lands.
 *
 * So we build the request ourselves and choose the resolution, sizing the window so the
 * tolerance is a constant number of *screen* pixels at any zoom:
 *
 *     tolerance_ground = MAPSERVER_TOLERANCE_PX * resolution
 *     want:              CURSOR_TOLERANCE_PX * ground_metres_per_pixel
 *     => resolution =    CURSOR_TOLERANCE_PX * mpp / MAPSERVER_TOLERANCE_PX
 *
 * This is installed as the provider's own `pickFeatures`, not as a separate click handler, so
 * Cesium keeps deciding WHICH layer answers a click: it walks the imagery stack top-down and
 * takes the first hit. A handler of our own would fire on every click and clobber Cesium's
 * in-flight pick of whatever layer is actually on top.
 */
const CURSOR_TOLERANCE_PX = 12;
const MAPSERVER_TOLERANCE_PX = 3;
/**
 * Query at the display scale rather than widening the window. Necessary for services whose
 * layers have scale limits — see `installMapServerFeaturePicking`.
 */
export const NATIVE_TOLERANCE_PX = MAPSERVER_TOLERANCE_PX;
/** Odd, so the click sits exactly on the centre pixel. */
const QUERY_WINDOW_PX = 101;
/** Per layer, so there are candidates to rank; the nearest is shown first. */
const FEATURE_COUNT = 5;

/**
 * Web Mercator metres per pixel at level 0: the projection spans 2·π·a metres across one
 * 256 px tile. Cesium's WMS provider defaults to a `WebMercatorTilingScheme` with 256 px tiles
 * and a single tile at level 0, and these layers do not override either.
 */
const PROJECTED_METRES_PER_PIXEL_AT_LEVEL_0 = (2 * Math.PI * 6378137) / 256;

const projection = new WebMercatorProjection();

/** MapServer bookkeeping rather than anything a reader wants. */
const HIDDEN_PROPERTIES = new Set(['boundedBy', 'msGeometry']);

interface WmsFeature {
	layerTitle: string;
	name: string;
	properties: Record<string, string>;
	/** Centre of gml:boundedBy, in EPSG:3857. Absent if the service omitted it. */
	centre: { x: number; y: number } | undefined;
}

/**
 * Half the query window, in projected metres.
 *
 * The ground resolution we want is `CURSOR_TOLERANCE_PX * groundMetresPerPixel /
 * MAPSERVER_TOLERANCE_PX`, and Web Mercator stretches ground metres by 1/cos(latitude) — but
 * the ground resolution at this level carries a factor cos(latitude) of its own, so the two
 * cancel and the window is a pure function of the tile level.
 */
const halfWindowMetres = (level: number, tolerancePx: number): number =>
	(QUERY_WINDOW_PX * tolerancePx * (PROJECTED_METRES_PER_PIXEL_AT_LEVEL_0 / 2 ** level)) /
	(2 * MAPSERVER_TOLERANCE_PX);

const buildFeatureInfoUrl = (
	provider: WebMapServiceImageryProvider,
	centre: Cartesian3,
	level: number,
	queryLayers: string,
	tolerancePx: number
): string => {
	const half = halfWindowMetres(level, tolerancePx);

	const url = new URL(provider.url);
	url.search = '';
	const params: Record<string, string> = {
		SERVICE: 'WMS',
		VERSION: '1.3.0',
		REQUEST: 'GetFeatureInfo',
		LAYERS: provider.layers,
		QUERY_LAYERS: queryLayers,
		// MapServer 8 rejects GetFeatureInfo without STYLES.
		STYLES: '',
		INFO_FORMAT: 'application/vnd.ogc.gml',
		FEATURE_COUNT: String(FEATURE_COUNT),
		CRS: 'EPSG:3857',
		BBOX: `${centre.x - half},${centre.y - half},${centre.x + half},${centre.y + half}`,
		WIDTH: String(QUERY_WINDOW_PX),
		HEIGHT: String(QUERY_WINDOW_PX),
		I: String((QUERY_WINDOW_PX - 1) / 2),
		J: String((QUERY_WINDOW_PX - 1) / 2)
	};
	Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
	return url.toString();
};

/** `<gml:Box srsName="EPSG:3857"><gml:coordinates>x,y x,y</gml:coordinates>` */
const readBoundingBoxCentre = (feature: Element): { x: number; y: number } | undefined => {
	const text = feature.getElementsByTagName('gml:coordinates')[0]?.textContent?.trim();
	if (!text) return undefined;
	const corners = text.split(/\s+/).map((pair) => pair.split(',').map(Number));
	const valid = corners.filter((c) => c.length >= 2 && c.every(Number.isFinite));
	if (valid.length === 0) return undefined;
	const xs = valid.map((c) => c[0]);
	const ys = valid.map((c) => c[1]);
	return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
};

/** MapServer answers with `<msGMLOutput><X_layer><X_feature>…`, always well-formed. */
const parseMsGmlOutput = (xml: string): WmsFeature[] => {
	const document = new DOMParser().parseFromString(xml, 'text/xml');
	if (document.querySelector('parsererror')) return [];

	const features: WmsFeature[] = [];
	for (const layer of Array.from(document.documentElement.children)) {
		const layerTitle = layer.getElementsByTagName('gml:name')[0]?.textContent?.trim() ?? '';
		for (const element of Array.from(layer.children)) {
			if (!element.localName.endsWith('_feature')) continue;
			const properties: Record<string, string> = {};
			for (const property of Array.from(element.children)) {
				if (property.prefix === 'gml' || HIDDEN_PROPERTIES.has(property.localName)) continue;
				const value = property.textContent?.trim();
				if (value) properties[property.localName] = value;
			}
			if (Object.keys(properties).length === 0) continue;
			features.push({
				layerTitle,
				name: properties.navn ?? properties.lokalitetsart ?? layerTitle,
				properties,
				centre: readBoundingBoxCentre(element)
			});
		}
	}
	return features;
};

const escapeHtml = (value: string): string =>
	value.replace(
		/[&<>"']/g,
		(character) =>
			({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ??
			character
	);

/**
 * The same markup `ImageryLayerFeatureInfo.configureDescriptionFromProperties` produces, so the
 * feature-info modal can parse every layer's description the same way — plus the layer title,
 * which Cesium has no way to carry (it drops `ImageryLayerFeatureInfo.imageryLayer` when it
 * builds the entity).
 *
 * Built here rather than by Cesium's own generator because that one interpolates values
 * unescaped, and these come from a third-party service.
 */
const toDescriptionHtml = (feature: WmsFeature): string => {
	const rows = Object.entries(feature.properties)
		.map(([key, value]) => `<tr><td>${escapeHtml(key)}</td><td>${escapeHtml(value)}</td></tr>`)
		.join('');
	return (
		`<p class="sg-layer-title">${escapeHtml(feature.layerTitle)}</p>` +
		`<table class="cesium-infoBox-defaultTable">${rows}</table>`
	);
};

/**
 * Nearest to the click first. A buffered query returns several candidates, and Cesium shows
 * `features[0]`, so the ranking is what makes it show the one the user aimed at.
 */
const byDistanceTo = (centre: Cartesian3) => (a: WmsFeature, b: WmsFeature) => {
	// Features without a bounding box sort last, but are still offered.
	const distance = (feature: WmsFeature) =>
		feature.centre
			? Math.hypot(feature.centre.x - centre.x, feature.centre.y - centre.y)
			: Number.MAX_VALUE;
	return distance(a) - distance(b);
};

/**
 * `position` is deliberately left unset, so Cesium falls back to the point the click ray hit
 * the globe — the marker then lands exactly under the cursor.
 *
 * Setting it from the feature's bounding-box centre is tempting, but that centre is a 2D
 * Web Mercator coordinate with no height: unprojecting it puts the marker on the ellipsoid,
 * i.e. at sea level, so over any real terrain it floats away from both the click and the
 * feature. Getting it right needs a terrain height sample, which is not available here —
 * Cesium calls `pickFeatures` without a viewer.
 */
const toFeatureInfo = (feature: WmsFeature): ImageryLayerFeatureInfo => {
	const info = new ImageryLayerFeatureInfo();
	info.name = feature.name;
	info.description = toDescriptionHtml(feature);
	info.data = feature.properties;
	return info;
};

const requestFeatures = async (
	provider: WebMapServiceImageryProvider,
	centre: Cartesian3,
	level: number,
	queryLayers: string,
	tolerancePx: number
): Promise<WmsFeature[]> => {
	try {
		const response = await fetch(
			buildFeatureInfoUrl(provider, centre, level, queryLayers, tolerancePx)
		);
		if (!response.ok) return [];
		return parseMsGmlOutput(await response.text());
	} catch (e) {
		console.warn('GetFeatureInfo failed for', provider.url, e);
		return [];
	}
};

/**
 * Installs our GetFeatureInfo across every provider backing one meta layer.
 *
 * Cesium's own request does not work against these MapServer services for two reasons: its
 * tolerance collapses as you zoom in (see the file header), and its INFO_FORMAT defaults —
 * `application/json`, `text/xml`, `text/html` — are not what these services offer. Norwegian
 * MapServer endpoints answer `application/vnd.ogc.gml`, which is what we ask for.
 *
 * A meta layer's providers are ranked TOGETHER: Cesium concatenates each provider's features in
 * stack order and shows the first, and Kulturminner stacks the large brannvern and kulturmiljø
 * polygons above the lokalitet and enkeltminne points. Ranked per provider, a polygon the click
 * merely falls inside would beat the point the user actually aimed at. So one provider — the
 * top-most, which is where the group sits in the imagery stack — answers for all of them, and
 * the rest are silenced. Silenced with a no-op rather than `enablePickFeatures = false`, because
 * the map page flips that flag back on for every provider whenever tools and selection change.
 *
 * `queryLayers` overrides what is asked about, for services whose drawn layer cannot be queried
 * — Matrikkel draws the `matrikkelkart` group, but querying that group makes MapServer fail with
 * `msShapefileOpen(): Unable to access file`, because the group has no data source of its own.
 *
 * `tolerancePx` is how much slack a click gets, in screen pixels. The default suits sparse POINT
 * data, where a near miss should still count. Pass `NATIVE_TOLERANCE_PX` for services whose
 * layers are scale-gated: widening the window coarsens the query's scale, and MapServer then
 * skips any layer that would not draw at it — Matrikkel returns nothing at the default.
 */
export const installMapServerFeaturePicking =
	({
		queryLayers,
		tolerancePx = CURSOR_TOLERANCE_PX
	}: { queryLayers?: string; tolerancePx?: number } = {}) =>
	(providers: WebMapServiceImageryProvider[]): void => {
		const speaker = providers.at(-1);
		if (!speaker) return;

		for (const provider of providers) {
			if (provider !== speaker) provider.pickFeatures = () => undefined;
		}

		speaker.pickFeatures = (
			_x: number,
			_y: number,
			level: number,
			longitude: number,
			latitude: number
		): Promise<ImageryLayerFeatureInfo[]> | undefined => {
			// Cesium's own WMS pickFeatures honours this flag; replacing the method means honouring
			// it here instead. The map page turns it off while a tool is active.
			//
			// undefined rather than an empty array, because that is the signal Cesium checks to
			// decide whether a click picks imagery AT ALL — resolving to [] would still put it into
			// the two-phase «Loading...» selection and end on a "no features" entity.
			if (!speaker.enablePickFeatures) return undefined;

			const centre = projection.project(new Cartographic(longitude, latitude));

			return Promise.all(
				providers.map((provider) =>
					requestFeatures(provider, centre, level, queryLayers ?? provider.layers, tolerancePx)
				)
			).then((perService) => perService.flat().sort(byDistanceTo(centre)).map(toFeatureInfo));
		};
	};
